/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { startPayment, StartPaymentProcessDTO } from 'api/simple_payment'
import { EventCardListing } from 'components/eventCard/eventCardListing'
import { BulkTicketControls } from 'components/ticket/bulkTicketControls'
import { SeatTicketControls } from 'components/ticket/seatTicketControls'
import { Container, MessageContainer, PageTitle, SubSectionContainer } from 'components/ticket/ticket.style'
import { useTickets } from 'hooks/user/useTickets'
import { useTicketStatusesInterval, useTransactionStatusInterval } from 'hooks/user/useTransactionStatus'
import { Message } from 'primereact/message'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { EventDetails } from 'types/Event'

interface Props {
    currentEvent: EventDetails
}

export const DownloadTickets = ({ currentEvent }: Props) => {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const selectedSeats = queryParams.get('seats')?.split(',') ?? []
    const { eventId } = useParams<{ eventId: string }>()

    const { isPaymentFinished, isPendingStatus, isErrorStatus, isCancelledStatus, transactionId } = useTransactionStatusInterval()
    const { areAllTicketsSold, areAnyTicketsInPayment, isAnyFailed } = useTicketStatusesInterval(selectedSeats, eventId!)

    const { handlePrintTicket, handleDownloadTicket, handlePrintAllTickets, handleDownloadAllTickets, loadingSeatIds, isLoadingAll } =
        useTickets(currentEvent, selectedSeats)

    const [activeLoadingKey, setActiveLoadingKey] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const retryLastPayment = async () => {
        const data = sessionStorage.getItem('lastPaymentData')
        if (!data) {
            alert('Nincs korábbi fizetési adat elmentve.')
            return
        }

        try {
            const request: StartPaymentProcessDTO = JSON.parse(data)
            const response = await startPayment(request)
            const transactionId = response.payment.transactionId
            sessionStorage.setItem('transaction_id', transactionId?.toString())

            window.location.href = response.payment.paymentUrl
        } catch (err) {
            console.error('Fizetés újrapróbálása sikertelen:', err)
            alert('A fizetés újrapróbálása nem sikerült.')
        }
    }

    const wrapWithErrorHandler = (fn: (...args: any[]) => Promise<any>, msg: string) => {
        return async (...args: any[]) => {
            setErrorMessage(null)
            try {
                await fn(...args)
            } catch (error) {
                console.error(error)
                setErrorMessage(msg)
            }
        }
    }

    const wrappedHandleDownloadTicket = useMemo(
        () => wrapWithErrorHandler(handleDownloadTicket, 'Jegy letöltése sikertelen. Kérjük próbálja újra később.'),
        [handleDownloadTicket],
    )
    const wrappedHandlePrintTicket = useMemo(
        () => wrapWithErrorHandler(handlePrintTicket, 'Jegy nyomtatása sikertelen. Kérjük próbálja újra később.'),
        [handlePrintTicket],
    )
    const wrappedHandleDownloadAllTickets = useMemo(
        () => wrapWithErrorHandler(handleDownloadAllTickets, 'Jegyek letöltése sikertelen. Kérjük próbálja újra később.'),
        [handleDownloadAllTickets],
    )
    const wrappedHandlePrintAllTickets = useMemo(
        () => wrapWithErrorHandler(handlePrintAllTickets, 'Jegyek nyomtatása sikertelen. Kérjük próbálja újra később.'),
        [handlePrintAllTickets],
    )

    const canShowButtons = isPaymentFinished && areAllTicketsSold

    return (
        <Container>
            {currentEvent && selectedSeats.length > 0 ? (
                <>
                    <PageTitle>Jegyek</PageTitle>
                    {currentEvent.event_id && <EventCardListing eventId={currentEvent.event_id} showPurchaseButton={false} />}
                    <SubSectionContainer>
                        {canShowButtons && (
                            <>
                                <SeatTicketControls
                                    currentEvent={currentEvent}
                                    selectedSeats={selectedSeats}
                                    activeLoadingKey={activeLoadingKey}
                                    setActiveLoadingKey={setActiveLoadingKey}
                                    handlePrintTicket={wrappedHandlePrintTicket}
                                    handleDownloadTicket={wrappedHandleDownloadTicket}
                                    areAllTicketsSold={areAllTicketsSold}
                                />
                                <BulkTicketControls
                                    activeLoadingKey={activeLoadingKey}
                                    setActiveLoadingKey={setActiveLoadingKey}
                                    handlePrintAllTickets={wrappedHandlePrintAllTickets}
                                    handleDownloadAllTickets={wrappedHandleDownloadAllTickets}
                                    areAllTicketsSold={areAllTicketsSold}
                                />
                            </>
                        )}

                        {isPendingStatus && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
                                <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                                <div>A fizetés folyamatban...</div>
                            </div>
                        )}

                        {isPaymentFinished && transactionId && (
                            <MessageContainer>
                                <Message severity="success" text={`Sikeres fizetés! SimplePay tranzakció azonosító: ${transactionId}`} />
                            </MessageContainer>
                        )}

                        {isCancelledStatus && (
                            <MessageContainer>
                                <Message severity="warn" text="Ön megszakította a fizetést." />
                            </MessageContainer>
                        )}

                        {isErrorStatus && transactionId && (
                            <MessageContainer>
                                <Message
                                    severity="error"
                                    text={
                                        <>
                                            <div>
                                                <strong>Sikertelen tranzakció.</strong>
                                            </div>
                                            <div>SimplePay tranzakció azonosító: {transactionId}</div>
                                            <div>
                                                Kérjük, ellenőrizze a tranzakció során megadott adatok helyességét. Amennyiben minden adatot
                                                helyesen adott meg, a visszautasítás okának kivizsgálása kapcsán kérjük, szíveskedjen
                                                kapcsolatba lépni kártyakibocsátó bankjával.
                                            </div>
                                            <button onClick={retryLastPayment} style={{ marginTop: '1rem' }}>
                                                {'Fizetés újrapróbálása'}
                                            </button>
                                        </>
                                    }
                                />
                            </MessageContainer>
                        )}

                        {errorMessage && (
                            <MessageContainer>
                                <Message severity="error" text={errorMessage} />
                            </MessageContainer>
                        )}
                    </SubSectionContainer>
                </>
            ) : (
                <Message severity="warn" text="Nincsenek kiválasztott jegyek vagy esemény." />
            )}
        </Container>
    )
}
